import { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  IconButton,
  Alert,
  CircularProgress,
  FormHelperText,
  SelectChangeEvent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../services/api';
import { TaskType, ApiResponse, User } from '../types';
import { AxiosError } from 'axios';
import { TASK_TYPE_CONFIG, TaskTypeConfigItem } from '../constants/taskConfig';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface FormErrors {
  taskType?: string;
  title?: string;
  description?: string;
  assignedTo?: string;
}

interface TaskCreateProps {
  onBack: () => void;
  onTaskCreated?: () => void;
}

function TaskCreate({ onBack, onTaskCreated }: TaskCreateProps) {
  // Form state
  const [taskType, setTaskType] = useState<TaskType | ''>('');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [assignedTo, setAssignedTo] = useState<string[]>([]);

  // Data state
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(true);

  // Form state
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  // Validation errors
  const [errors, setErrors] = useState<FormErrors>({});

  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async (): Promise<void> => {
      try {
        const response = await api.get<ApiResponse<User[]>>('users/list');
        setUsers(response.data.results || []);
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setError('Failed to load users list.');
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!taskType) {
      newErrors.taskType = 'Task type is required';
    }
    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (assignedTo.length === 0) {
      newErrors.assignedTo = 'At least one user must be assigned';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        type: taskType,
        assignedTo: assignedTo.map((id) => ({ id })),
      };

      await api.post('tasks/create', payload);
      setSuccess(true);

      // Notify parent and go back after short delay
      setTimeout(() => {
        if (onTaskCreated) onTaskCreated();
        if (onBack) onBack();
      }, 1500);
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      console.error('Failed to create task:', err);
      setError(axiosError.response?.data?.message || 'Failed to create task. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={onBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="h1">
              Create New Task
            </Typography>
          </Box>
        </Paper>

        {/* Success Message */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Task created successfully! Redirecting...
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Paper elevation={2} sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            {/* Task Type */}
            <FormControl fullWidth error={Boolean(errors.taskType)} sx={{ mb: 3 }}>
              <InputLabel id="task-type-label">Task Type</InputLabel>
              <Select
                labelId="task-type-label"
                value={taskType}
                label="Task Type"
                onChange={(e: SelectChangeEvent) => setTaskType(e.target.value as TaskType | '')}
              >
                {(Object.entries(TASK_TYPE_CONFIG) as [TaskType, TaskTypeConfigItem][]).map(([type, config]) => (
                  <MenuItem key={type} value={type}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <config.icon size={20} />
                      <span>{config.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.taskType && <FormHelperText>{errors.taskType}</FormHelperText>}
            </FormControl>

            {/* Title */}
            <TextField
              fullWidth
              label="Task Title"
              value={title}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
              error={Boolean(errors.title)}
              helperText={errors.title}
              sx={{ mb: 3 }}
            />

            {/* Description */}
            <TextField
              fullWidth
              label="Task Description"
              value={description}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
              error={Boolean(errors.description)}
              helperText={errors.description}
              multiline
              rows={3}
              sx={{ mb: 3 }}
            />

            {/* Assigned To */}
            <FormControl fullWidth error={Boolean(errors.assignedTo)} sx={{ mb: 3 }}>
              <InputLabel id="assigned-to-label">Assigned To</InputLabel>
              <Select<string[]>
                labelId="assigned-to-label"
                multiple
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value as string[])}
                input={<OutlinedInput label="Assigned To" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((userId) => {
                      const user = users.find((u) => u.id === userId);
                      return (
                        <Chip
                          key={userId}
                          label={user ? `${user.firstName} ${user.lastName}` : userId}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
                disabled={loadingUsers}
              >
                {loadingUsers ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} /> Loading users...
                  </MenuItem>
                ) : (
                  users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          @{user.username} - {user.roles?.join(', ')}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.assignedTo && <FormHelperText>{errors.assignedTo}</FormHelperText>}
            </FormControl>

            {/* Submit Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={onBack}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || success}
                sx={{ minWidth: 120 }}
              >
                {submitting ? <CircularProgress size={24} /> : 'Create Task'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}

export default TaskCreate;
